<section class="fj-logbook-hero" appHeroScrollCue>
        <div class="site-container fj-logbook-hero-grid">
            <div class="fj-log-cover">
                <p class="eyebrow">Georgetown home checkup</p>
                <h1>A calm, complete checkup before the next home decision.</h1>
                <p class="hero-lede">
                    GTX Inspections, LLC provides professional home inspection services across Georgetown,
                    Austin, and the surrounding Central Texas area. Roland Moreno, TREC# 23635, gives every
                    home the time, documentation, and plain-language explanation it deserves before the next property decision.
                </p>
                <div class="hero-actions">
                    <a class="ng-button ng-button-primary" routerLink="/contact">Schedule an inspection</a>
                    <a class="ng-button ng-button-ghost" href="tel:512-848-1316">Call/Text (512) 848-1316</a>
                </div>
                <div class="fj-credential-row" aria-label="GTX local trust signals">
                    <span>TREC# 23635</span>
                    <span>Licensed and bonded</span>
                    <span>Spanish-speaking inspector</span>
                    <span>Central Texas checkup support</span>
                </div>
            </div>

            <aside class="fj-hero-spread" aria-label="Field journal home inspection notes">
                <figure>
                    <img src="assets/images/design-a/kari-roland-door-field-journal.jpg" alt="Roland and Kari Moreno with GTX Inspections" />
                    <figcaption>
                        <span>Family-owned GTX</span>
                        <strong>Roland and Kari Moreno</strong>
                    </figcaption>
                </figure>
                <ol class="fj-margin-notes">
                    <li>
                        <span>01</span>
                        <strong>Share the home details</strong>
                        <small>Buying, selling, building, or maintaining a Central Texas home.</small>
                    </li>
                    <li>
                        <span>02</span>
                        <strong>Check visible systems</strong>
                        <small>Exterior, roof, attic, HVAC, interior, electrical, and plumbing are reviewed.</small>
                    </li>
                    <li>
                        <span>03</span>
                        <strong>Turn findings into next steps</strong>
                        <small>Your report helps you and your agent make informed decisions.</small>
                    </li>
                </ol>
            </aside>
        </div>
    </section>

    <ng-container *ngTemplateOutlet="thermalRevealSection"></ng-container>

    <section #sampleFindingSection class="sample-finding-section"
        [class.is-open]="sampleFindingOpened"
        aria-labelledby="sample-finding-title">
        <div class="site-container sample-finding-shell">
            <div class="sample-finding-heading">
                <p class="eyebrow">Sample inspection finding</p>
                <h2 id="sample-finding-title">Clear Findings. Practical Recommendations.</h2>
                <p>
                    GTX reports use photos, location details, plain-language explanations, and recommended next steps so
                    clients can understand what was observed and what to do next.
                </p>
            </div>

            <div class="finding-folder-shell" tabindex="0" (keydown)="onSampleFindingFolderKeydown($event)">
                <div class="finding-folder-back" aria-hidden="true">
                    <span>GTX Inspections</span>
                </div>

                <div class="finding-paper-stack" aria-live="polite"
                    [class.is-swapping]="sampleFindingPageSwapping"
                    [class.swap-next]="sampleFindingPageDirection === 'next'"
                    [class.swap-previous]="sampleFindingPageDirection === 'previous'">
                    <span class="finding-paper-offset finding-paper-offset-one" aria-hidden="true"></span>
                    <span class="finding-paper-offset finding-paper-offset-two" aria-hidden="true"></span>

                    <ng-container *ngIf="outgoingSampleFinding as outgoingFinding">
                        <ng-container *ngTemplateOutlet="findingPageTemplate; context: {
                            finding: outgoingFinding,
                            pageNumber: outgoingSampleFindingNumber,
                            pageState: 'outgoing'
                        }"></ng-container>
                    </ng-container>

                    <ng-container *ngTemplateOutlet="findingPageTemplate; context: {
                        finding: selectedSampleFinding,
                        pageNumber: selectedSampleFindingNumber,
                        pageState: sampleFindingPageSwapping ? 'incoming' : 'current'
                    }"></ng-container>

                    <ng-template #findingPageTemplate let-finding="finding" let-pageNumber="pageNumber" let-pageState="pageState">
                    <article class="finding-paper-page"
                        [class.is-current]="pageState === 'current'"
                        [class.is-incoming]="pageState === 'incoming'"
                        [class.is-outgoing]="pageState === 'outgoing'"
                        [attr.id]="'finding-page-' + finding.id"
                        [attr.aria-hidden]="pageState === 'outgoing' ? true : null"
                        [attr.aria-label]="finding.category + ' sample finding'">
                        <header class="finding-page-header">
                            <div>
                                <span class="finding-page-number">{{ pageNumber }} / {{ sampleFindingTotal }}</span>
                                <h3>{{ finding.title }}</h3>
                            </div>
                            <span class="finding-severity-badge" [ngClass]="'severity-' + finding.severityTone">
                                {{ finding.severity }}
                            </span>
                        </header>

                        <div class="finding-card">
                            <figure class="finding-photo">
                                <img [src]="finding.image" [alt]="finding.imageAlt"
                                    [style.object-position]="finding.imagePosition" loading="lazy" />
                                <span class="finding-photo-annotation"
                                    [style.left.%]="finding.annotationX"
                                    [style.top.%]="finding.annotationY"
                                    [style.width.%]="finding.annotationSize"
                                    [style.height.%]="finding.annotationSize"
                                    aria-hidden="true"></span>
                                <figcaption>{{ finding.photoCredit }}</figcaption>
                            </figure>

                            <div class="finding-content">
                                <div class="finding-overline">
                                    <span class="finding-category">{{ finding.category }}</span>
                                </div>

                                <p class="finding-location">
                                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                        <path d="M12 21s7-4.6 7-11a7 7 0 1 0-14 0c0 6.4 7 11 7 11Z"></path>
                                        <circle cx="12" cy="10" r="2.4"></circle>
                                    </svg>
                                    <span>{{ finding.location }}</span>
                                </p>

                                <div class="finding-body">
                                    <section [attr.aria-labelledby]="'finding-explanation-title-' + finding.id">
                                        <h4 [attr.id]="'finding-explanation-title-' + finding.id">Explanation</h4>
                                        <p>{{ finding.explanation }}</p>
                                    </section>

                                    <section class="finding-recommendation" [attr.aria-labelledby]="'finding-recommendation-title-' + finding.id">
                                        <h4 [attr.id]="'finding-recommendation-title-' + finding.id">Recommendation</h4>
                                        <p>{{ finding.recommendation }}</p>
                                    </section>
                                </div>

                                <div class="finding-chip-row" aria-label="Finding metadata">
                                    <span *ngFor="let chip of finding.metadata">{{ chip }}</span>
                                </div>
                            </div>
                        </div>
                    </article>
                    </ng-template>
                </div>

                <div class="finding-folder-front" aria-hidden="true">
                    <span></span>
                </div>
            </div>

            <div class="finding-folder-controls" aria-label="Sample finding pages">
                <button class="finding-arrow-button" type="button" aria-label="Show previous finding"
                    (click)="previousSampleFinding()">
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path d="M15 18 9 12l6-6"></path>
                    </svg>
                </button>
                <div class="finding-page-tabs" role="tablist" aria-label="Sample finding pages">
                    <button *ngFor="let finding of sampleFindings; let i = index; trackBy: trackSampleFinding"
                        type="button" role="tab"
                        [class.is-selected]="i === selectedSampleFindingIndex"
                        [attr.aria-selected]="i === selectedSampleFindingIndex"
                        [attr.aria-controls]="'finding-page-' + finding.id"
                        (click)="selectSampleFinding(i)">
                        <span>{{ finding.tabLabel }}</span>
                    </button>
                </div>
                <button class="finding-arrow-button" type="button" aria-label="Show next finding"
                    (click)="nextSampleFinding()">
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path d="m9 18 6-6-6-6"></path>
                    </svg>
                </button>
            </div>
        </div>
    </section>

    <section class="fj-quick-ledger" aria-label="GTX home health checkup summary">
        <div class="site-container">
            <article *ngFor="let stat of fieldStats">
                <span>{{ stat.kicker }}</span>
                <strong>{{ stat.value }}</strong>
                <small>{{ stat.label }}</small>
            </article>
        </div>
    </section>

    <section class="fj-dispatch-board">
        <div class="site-container">
            <div class="fj-decision-docket">
                <div class="fj-section-kicker">
                    <p class="eyebrow">Choose the checkup need</p>
                    <h2>Start with the home decision this inspection needs to support.</h2>
                    <p>
                        Pick the closest situation and GTX will shape the conversation around the timing, documents,
                        and inspection notes that matter most for that plan.
                    </p>
                </div>
                <div class="fj-docket-tabs" role="tablist" aria-label="Inspection need">
                    <button *ngFor="let need of needs; let i = index" type="button"
                        [class.is-selected]="i === selectedNeedIndex" [attr.aria-selected]="i === selectedNeedIndex"
                        (click)="selectNeed(i)">
                        {{ need.label }}
                    </button>
                </div>
                <article class="fj-selected-docket" aria-live="polite">
                    <img [src]="fieldJournalImage(selectedNeed.image)" [alt]="selectedNeed.label + ' inspection path'"
                        [style.object-position]="selectedNeed.imagePosition" loading="lazy" />
                    <div>
                        <span class="mini-label">{{ selectedNeed.deadline }}</span>
                        <h3>{{ selectedNeed.title }}</h3>
                        <p>{{ selectedNeed.description }}</p>
                        <dl>
                            <div>
                                <dt>Primary question</dt>
                                <dd>{{ selectedNeed.keyQuestion }}</dd>
                            </div>
                            <div>
                                <dt>Report focus</dt>
                                <dd>{{ selectedNeed.reportFocus }}</dd>
                            </div>
                        </dl>
                        <ul>
                            <li *ngFor="let item of selectedNeed.send">{{ item }}</li>
                        </ul>
                        <a class="ng-button ng-button-primary" routerLink="/contact">Start this request</a>
                    </div>
                </article>
            </div>
        </div>
    </section>

    <section class="fj-photo-field">
        <div class="site-container fj-photo-grid">
            <figure class="fj-focus-photo" aria-live="polite">
                <img [src]="selectedFocus.image" [alt]="selectedFocus.title"
                    [style.object-position]="selectedFocus.imagePosition" loading="lazy" />
                <figcaption>
                    <span>{{ selectedFocus.metric }}</span>
                    <strong>{{ selectedFocus.title }}</strong>
                    <p>{{ selectedFocus.note }}</p>
                </figcaption>
            </figure>
            <div class="fj-focus-copy">
                <p class="eyebrow">Visible condition notes</p>
                <h2>Each major system gets practical homeowner context.</h2>
                <p>
                    Roland reviews visible structural, roof, attic, HVAC, interior, electrical, and plumbing systems.
                    The goal is simple: help you understand the home before you make the next decision.
                </p>
                <div class="fj-focus-tabs" role="tablist" aria-label="Inspection focus gallery">
                    <button *ngFor="let focus of focusAreas; let i = index" type="button"
                        [class.is-selected]="i === selectedFocusIndex" [attr.aria-selected]="i === selectedFocusIndex"
                        (click)="selectFocus(i)">
                        {{ focus.label }}
                    </button>
                </div>
            </div>
        </div>
    </section>

    <section class="fj-family-handoff">
        <div class="site-container fj-family-handoff-grid">
            <img src="assets/images/design-a/kari-roland-georgetown-field-journal.jpg" alt="Roland and Kari Moreno in Georgetown" loading="lazy" />
            <div>
                <p class="eyebrow">Professional and approachable</p>
                <h2>Talk with a local inspection team that knows Central Texas homes.</h2>
                <p>
                    Roland brings years of construction and inspection experience. Kari helps keep scheduling,
                    communication, and client support friendly and organized.
                </p>
            </div>
            <div class="hero-actions">
                <a class="ng-button ng-button-primary" routerLink="/contact">Request scheduling help</a>
                <a class="ng-button ng-button-secondary" routerLink="/about">Meet Roland and Kari</a>
            </div>
        </div>
    </section>

<ng-template #thermalRevealSection>
<section class="thermal-reveal-section" aria-labelledby="thermal-reveal-title">
    <div class="site-container thermal-reveal-shell">
        <div class="thermal-reveal-heading">
            <div>
                <p class="eyebrow">Thermal imaging reveal</p>
                <h2 id="thermal-reveal-title">See What Standard Photos Can Miss</h2>
            </div>
            <p>
                Thermal imaging helps GTX compare surface temperature differences across visible surfaces. It can guide
                further evaluation for possible moisture, insulation gaps, and air leakage, but it does not see through
                walls or confirm hidden conditions by itself.
            </p>
        </div>

        <figure class="thermal-reveal-frame">
            <div class="thermal-reveal-stage">
                <img class="thermal-standard-image" src="assets/images/thermal-reveal-room.jpg"
                    alt="Bright unfurnished room with a ceiling fan, window, door, carpet, and baseboards for a thermal imaging comparison" loading="lazy" />
                <span class="thermal-view-label thermal-view-label-standard">Standard View</span>

                <div class="thermal-reveal-pane" [style.clip-path]="thermalClipPath">
                    <img class="thermal-image" src="assets/images/thermal-reveal-room.jpg" alt="" role="presentation" loading="lazy" />
                    <span class="thermal-color-map" aria-hidden="true"></span>
                    <span class="thermal-scan-texture" aria-hidden="true"></span>
                    <span class="thermal-view-label thermal-view-label-thermal">Thermal View</span>
                </div>

                <div class="thermal-hotspot-layer" aria-label="Thermal imaging example findings">
                    <button *ngFor="let hotspot of thermalRevealHotspots; let i = index; trackBy: trackThermalHotspot"
                        class="thermal-hotspot-button" type="button"
                        [class.is-selected]="i === selectedThermalHotspotIndex"
                        [style.left.%]="hotspot.x"
                        [style.top.%]="hotspot.y"
                        [attr.aria-pressed]="i === selectedThermalHotspotIndex"
                        [attr.aria-label]="'Show ' + hotspot.title"
                        (click)="selectThermalHotspot(i)">
                        <img class="thermal-hotspot-icon" src="assets/images/gtx-magnifying-glass.svg" alt="" aria-hidden="true" />
                        <strong>{{ hotspot.label }}</strong>
                    </button>
                </div>

                <input class="thermal-reveal-range" type="range" min="8" max="92" step="1"
                    [value]="thermalRevealValue"
                    [attr.aria-valuetext]="thermalRevealText"
                    aria-label="Reveal Thermal View over Standard View"
                    (input)="setThermalReveal($any($event.target).value)"
                    (keydown)="onThermalSliderKeydown($event)" />

                <span class="thermal-reveal-handle" [style.left.%]="thermalRevealValue" aria-hidden="true">
                    <span></span>
                </span>

                <aside *ngIf="selectedThermalHotspot as activeThermalHotspot"
                    class="thermal-popover"
                    [style.left.%]="thermalPopoverLeft"
                    [style.top.%]="thermalPopoverTop"
                    aria-live="polite">
                    <span>Possible finding</span>
                    <h3>{{ activeThermalHotspot.title }}</h3>
                    <p>{{ activeThermalHotspot.summary }}</p>
                    <p>{{ activeThermalHotspot.detail }}</p>
                </aside>
            </div>
        </figure>

        <div class="thermal-reveal-cta">
            <a class="ng-button ng-button-primary" routerLink="/contact">Schedule an Inspection</a>
        </div>
    </div>
</section>
</ng-template>
